<template>
  <div class="card">
    <div class="card-header">
      <div>
        <h2>{{ $t('pos.mappings.title') }}</h2>
        <p class="card-subtitle">{{ $t('pos.mappings.subtitle') }}</p>
      </div>
      <BaseButton
        v-if="!readOnly && hasIntegration"
        type="button"
        variant="secondary"
        icon="fa-plus"
        @click="openCreate"
      >
        {{ $t('pos.mappings.addMapping') }}
      </BaseButton>
    </div>

    <div class="card-body">
      <div v-if="loading" class="loading-state">
        <AppPageLoader size="sm" direction="row">{{ $t('general.loading') }}</AppPageLoader>
      </div>

      <div v-else-if="!hasIntegration" class="empty-state">
        <font-awesome-icon icon="fa-link-slash" class="empty-icon" />
        <h3>{{ $t('pos.mappings.noIntegrationTitle') }}</h3>
        <p>{{ $t('pos.mappings.noIntegrationDescription') }}</p>
      </div>

      <div v-else-if="mappings.length === 0" class="empty-state">
        <font-awesome-icon icon="fa-arrows-left-right-to-line" class="empty-icon" />
        <h3>{{ $t('pos.mappings.emptyTitle') }}</h3>
        <p>{{ $t('pos.mappings.emptyDescription') }}</p>
      </div>

      <div v-else class="table-wrap">
        <table class="mappings-table">
          <thead>
            <tr>
              <th>{{ $t('pos.mappings.colMenuItem') }}</th>
              <th>{{ $t('pos.mappings.colPOSArticleCode') }}</th>
              <th>{{ $t('pos.mappings.colPOSIds') }}</th>
              <th class="col-availability">
                <span class="col-availability-label">
                  {{ $t('pos.mappings.inApp') }}
                  <button
                    type="button"
                    class="info-btn"
                    :aria-label="$t('pos.mappings.availabilityOn')"
                    :data-tooltip="$t('pos.mappings.availabilityOn')"
                  >
                    <font-awesome-icon icon="fa-info-circle" />
                  </button>
                </span>
              </th>
              <th class="col-actions">{{ $t('pos.mappings.colActions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="m in mappings" :key="m.uuid" :class="{ 'row-off': m.available === false }">
              <td>
                <div class="menu-item-name">{{ m.menu_item_name || $t('pos.mappings.menuItemMissing') }}</div>
                <div class="menu-item-uuid">{{ m.menu_item_id }}</div>
              </td>
              <td><code class="mono">{{ m.pos_article_code || '—' }}</code></td>
              <td class="col-ids">
                <details class="id-details">
                  <summary>
                    <code class="mono">{{ shortPosId(m.pos_article_id) }}</code>
                  </summary>
                  <div class="id-full">
                    <div class="id-line">
                      <span class="id-label">{{ $t('pos.mappings.colPOSArticleID') }}</span>
                      <code class="mono">{{ m.pos_article_id }}</code>
                    </div>
                    <div class="id-line">
                      <span class="id-label">{{ $t('pos.mappings.colPOSCategoryID') }}</span>
                      <code class="mono">{{ m.pos_category_id || '—' }}</code>
                    </div>
                  </div>
                </details>
              </td>
              <td class="col-availability">
                <label class="availability-switch">
                  <input
                    class="availability-input"
                    type="checkbox"
                    :checked="m.available !== false"
                    :disabled="readOnly || availabilitySavingId === m.uuid"
                    :aria-label="$t('pos.mappings.inApp')"
                    @change="onAvailabilityToggle(m, $event)"
                  />
                  <span class="availability-track" aria-hidden="true">
                    <span class="availability-thumb"></span>
                  </span>
                </label>
              </td>
              <td class="col-actions">
                <BaseButton
                  v-if="!readOnly"
                  type="button"
                  variant="secondary"
                  icon="fa-pen"
                  @click="openEdit(m)"
                >
                  {{ $t('common.edit') }}
                </BaseButton>
                <BaseButton
                  v-if="!readOnly"
                  type="button"
                  variant="danger"
                  icon="fa-trash"
                  @click="confirmDelete(m)"
                >
                  {{ $t('common.delete') }}
                </BaseButton>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="errorMessage" class="error-banner">
        <font-awesome-icon icon="fa-triangle-exclamation" />
        <span>{{ errorMessage }}</span>
      </div>
    </div>

    <div v-if="showFormModal" class="modal-overlay" @click.self="closeFormModal">
      <div ref="mappingModalRef" class="modal-content modal-wide">
        <h3>{{ editingMapping ? $t('pos.mappings.editTitle') : $t('pos.mappings.addTitle') }}</h3>
        <p class="form-help">{{ $t('pos.mappings.formDescription') }}</p>

        <div class="form-group" data-field="menu_item_id">
          <label class="form-label" for="mapping-menu-item">{{ $t('pos.mappings.colMenuItem') }} <span class="required">*</span></label>
          <select
            id="mapping-menu-item"
            v-model="formMenuItemId"
            class="form-select"
            :class="{ 'error-input': formErrors.menu_item_id }"
            :disabled="!!editingMapping || menuItemsLoading"
          >
            <option value="" disabled>
              {{ menuItemsLoading ? $t('general.loading') : $t('pos.mappings.selectMenuItem') }}
            </option>
            <option
              v-for="opt in menuItemOptions"
              :key="opt.uuid"
              :value="opt.uuid"
              :disabled="!editingMapping && existingMappedItemIds.has(opt.uuid)"
            >
              {{ opt.categoryName ? `${opt.categoryName} — ${opt.name}` : opt.name }}
              {{ !editingMapping && existingMappedItemIds.has(opt.uuid) ? `· ${$t('pos.mappings.alreadyMapped')}` : '' }}
            </option>
          </select>
          <div v-if="formErrors.menu_item_id" class="form-error">{{ formErrors.menu_item_id }}</div>
        </div>

        <div class="form-group" data-field="pos_article_id">
          <label class="form-label" for="mapping-article-id">{{ $t('pos.mappings.colPOSArticleID') }} <span class="required">*</span></label>
          <input
            id="mapping-article-id"
            v-model="formArticleId"
            type="text"
            class="form-input"
            :class="{ 'error-input': formErrors.pos_article_id }"
            spellcheck="false"
            autocomplete="off"
            :placeholder="$t('pos.mappings.articleIdPlaceholder')"
          />
          <div v-if="formErrors.pos_article_id" class="form-error">{{ formErrors.pos_article_id }}</div>
          <p class="form-help">{{ $t('pos.mappings.articleIdHelp') }}</p>
        </div>

        <div class="form-group">
          <label class="form-label">{{ $t('pos.mappings.colPOSArticleCode') }}</label>
          <input
            v-model="formArticleCode"
            type="text"
            class="form-input"
            spellcheck="false"
            autocomplete="off"
            :placeholder="$t('pos.mappings.articleCodePlaceholder')"
          />
          <p class="form-help">{{ $t('pos.mappings.articleCodeHelp') }}</p>
        </div>

        <div class="form-group">
          <label class="form-label">{{ $t('pos.mappings.colPOSCategoryID') }}</label>
          <input
            v-model="formCategoryId"
            type="text"
            class="form-input"
            spellcheck="false"
            autocomplete="off"
            :placeholder="$t('pos.mappings.categoryIdPlaceholder')"
          />
        </div>

        <div v-if="formError" class="error-banner">
          <font-awesome-icon icon="fa-triangle-exclamation" />
          <span>{{ formError }}</span>
        </div>

      </div>
    </div>

    <div v-if="deletingTarget" class="modal-overlay" @click.self="deletingTarget = null">
      <div class="modal-content">
        <h3>{{ $t('pos.mappings.deleteTitle') }}</h3>
        <p>{{ $t('pos.mappings.deleteWarning') }}</p>
        <p class="muted">
          <strong>{{ deletingTarget.menu_item_name || deletingTarget.menu_item_id }}</strong>
          <span> ↔ </span>
          <code class="mono">{{ deletingTarget.pos_article_id }}</code>
        </p>
        <div class="modal-actions">
          <BaseButton variant="secondary" type="button" @click="deletingTarget = null">
            {{ $t('general.cancel') }}
          </BaseButton>
          <BaseButton
            variant="danger"
            :loading="deleting"
            type="button"
            @click="deleteMapping"
          >
            {{ $t('general.delete') }}
          </BaseButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseButton from '@/components/shared/BaseButton.vue';
import {
  posApi,
  menuApi,
  type POSMenuMapping,
  type MenuCategoryListResponse,
} from '@/services/api';
import type { MenuCategory, MenuItem } from '@/types/menu';
import { getApiErrorMessage } from '@/utils/apiErrors';
import { useFormFloatingActions } from '@/composables/useFormFloatingActions';
import { scrollToFirstFormError, stopGuidedFormErrorNavigation } from '@/utils/scrollToFirstFormError';
import { unwrapList } from '@/utils/apiResponse';

const mappingModalRef = ref<HTMLElement | null>(null);

interface Props {
  locationId: string;
  establishmentId: string;
  readOnly?: boolean;
}

const props = defineProps<Props>();
const { t } = useI18n();

const loading = ref(true);
const mappings = ref<POSMenuMapping[]>([]);
const errorMessage = ref<string | null>(null);
const hasIntegration = ref(true);

const showFormModal = ref(false);
const editingMapping = ref<POSMenuMapping | null>(null);
const formMenuItemId = ref('');
const formArticleId = ref('');
const formArticleCode = ref('');
const formCategoryId = ref('');
const formErrors = reactive<Record<string, string>>({
  menu_item_id: '',
  pos_article_id: '',
});
const formError = ref<string | null>(null);
const saving = ref(false);

const deletingTarget = ref<POSMenuMapping | null>(null);
const deleting = ref(false);
const availabilitySavingId = ref<string | null>(null);

function shortPosId(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length <= 14) {
    return trimmed;
  }
  return `${trimmed.slice(0, 8)}…`;
}

const menuItemsLoading = ref(false);
const menuItemOptions = ref<Array<{ uuid: string; name: string; categoryName: string }>>([]);

const existingMappedItemIds = computed(() => new Set(mappings.value.map((m) => m.menu_item_id)));

async function loadMappings() {
  if (!props.locationId) {
    hasIntegration.value = false;
    mappings.value = [];
    loading.value = false;
    return;
  }
  loading.value = true;
  errorMessage.value = null;
  try {
    const integration = await posApi.getIntegration(props.locationId);
    hasIntegration.value = Boolean(integration?.uuid);
    if (!hasIntegration.value) {
      mappings.value = [];
      return;
    }
    const data = await posApi.listMenuMappings(props.locationId);
    const rows = Array.isArray(data) ? data : [];
    mappings.value = rows.map((row) => ({ ...row, available: row.available !== false }));
  } catch (err: any) {
    if (err?.response?.status === 404) {
      hasIntegration.value = false;
      mappings.value = [];
    } else {
      errorMessage.value = getApiErrorMessage(err, t('pos.mappings.errorLoading'));
    }
  } finally {
    loading.value = false;
  }
}

async function loadMenuItems() {
  if (!props.establishmentId) {
    return;
  }
  menuItemsLoading.value = true;
  try {
    const result = await menuApi.getCategories(props.establishmentId, {
      include_items: true,
      page_size: 500,
      location_uuid: props.locationId,
    });
    const categories: MenuCategory[] = unwrapList<MenuCategory>(result);
    const flattened: Array<{ uuid: string; name: string; categoryName: string }> = [];
    for (const cat of categories) {
      const items: MenuItem[] = cat.menu_items ?? [];
      for (const item of items) {
        flattened.push({
          uuid: item.uuid,
          name: item.name,
          categoryName: cat.name,
        });
      }
    }
    flattened.sort((a, b) => a.categoryName.localeCompare(b.categoryName) || a.name.localeCompare(b.name));
    menuItemOptions.value = flattened;
  } catch (err: any) {
    errorMessage.value = getApiErrorMessage(err, t('pos.mappings.errorLoadingMenu'));
  } finally {
    menuItemsLoading.value = false;
  }
}

function resetForm() {
  editingMapping.value = null;
  formMenuItemId.value = '';
  formArticleId.value = '';
  formArticleCode.value = '';
  formCategoryId.value = '';
  formErrors.menu_item_id = '';
  formErrors.pos_article_id = '';
  formError.value = null;
}

function openCreate() {
  resetForm();
  showFormModal.value = true;
  if (menuItemOptions.value.length === 0) {
    void loadMenuItems();
  }
}

function ensureMenuItemOption(m: POSMenuMapping) {
  if (menuItemOptions.value.some((opt) => opt.uuid === m.menu_item_id)) {
    return;
  }
  menuItemOptions.value = [
    {
      uuid: m.menu_item_id,
      name: m.menu_item_name || m.menu_item_id,
      categoryName: '',
    },
    ...menuItemOptions.value,
  ];
}

function openEdit(m: POSMenuMapping) {
  editingMapping.value = m;
  formMenuItemId.value = m.menu_item_id;
  formArticleId.value = m.pos_article_id;
  formArticleCode.value = m.pos_article_code ?? '';
  formCategoryId.value = m.pos_category_id ?? '';
  formErrors.menu_item_id = '';
  formErrors.pos_article_id = '';
  formError.value = null;
  showFormModal.value = true;
  if (menuItemOptions.value.length === 0) {
    void loadMenuItems().finally(() => ensureMenuItemOption(m));
    return;
  }
  ensureMenuItemOption(m);
}

function closeFormModal() {
  showFormModal.value = false;
  resetForm();
}

function validateMappingForm(): boolean {
  formErrors.menu_item_id = '';
  formErrors.pos_article_id = '';

  let isValid = true;
  if (!formMenuItemId.value) {
    formErrors.menu_item_id = t('pos.mappings.selectMenuItem');
    isValid = false;
  }
  if (!formArticleId.value.trim()) {
    formErrors.pos_article_id = t('pos.mappings.articleIdRequired');
    isValid = false;
  }
  return isValid;
}

async function saveMapping() {
  if (!validateMappingForm()) {
    await scrollToFirstFormError(formErrors, {
      root: mappingModalRef.value,
      fieldOrder: ['menu_item_id', 'pos_article_id'],
      validate: validateMappingForm,
    });
    return;
  }

  stopGuidedFormErrorNavigation();

  saving.value = true;
  formError.value = null;
  try {
    await posApi.upsertMenuMapping(props.locationId, {
      menu_item_id: formMenuItemId.value,
      pos_article_id: formArticleId.value.trim(),
      pos_article_code: formArticleCode.value.trim() || undefined,
      pos_category_id: formCategoryId.value.trim() || undefined,
    });
    closeFormModal();
    await loadMappings();
  } catch (err: any) {
    formError.value = getApiErrorMessage(err, t('pos.mappings.errorSaving'));
  } finally {
    saving.value = false;
  }
}

useFormFloatingActions({
  validate: validateMappingForm,
  submit: saveMapping,
  cancel: () => closeFormModal(),
  formErrors,
  getSubmitLabel: () => t('general.save'),
  getLoading: () => saving.value,
  active: () => showFormModal.value,
});

async function onAvailabilityToggle(mapping: POSMenuMapping, event: Event) {
  const input = event.target as HTMLInputElement;
  const available = input.checked;
  if (props.readOnly || availabilitySavingId.value === mapping.uuid) {
    input.checked = mapping.available !== false;
    return;
  }

  const previous = mapping.available !== false;
  mapping.available = available;
  availabilitySavingId.value = mapping.uuid;
  errorMessage.value = null;
  try {
    const updated = await posApi.setMenuMappingAvailability(props.locationId, mapping.uuid, available);
    mapping.available = updated.available !== false;
  } catch (err: unknown) {
    mapping.available = previous;
    input.checked = previous;
    errorMessage.value = getApiErrorMessage(err, t('pos.mappings.errorAvailability'));
  } finally {
    availabilitySavingId.value = null;
  }
}

function confirmDelete(m: POSMenuMapping) {
  deletingTarget.value = m;
}

async function deleteMapping() {
  if (!deletingTarget.value) return;
  deleting.value = true;
  try {
    await posApi.deleteMenuMapping(props.locationId, deletingTarget.value.uuid);
    deletingTarget.value = null;
    await loadMappings();
  } catch (err: any) {
    errorMessage.value = getApiErrorMessage(err, t('pos.mappings.errorDeleting'));
  } finally {
    deleting.value = false;
  }
}

watch(
  () => [props.locationId, props.establishmentId] as const,
  () => {
    void loadMappings();
  },
  { immediate: true },
);
</script>

<style scoped>
.card {
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 2rem;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.card-header h2 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
}

.card-subtitle {
  margin: 0.25rem 0 0;
  color: #6b7280;
  font-size: 0.875rem;
}

.card-body {
  padding: 1.5rem;
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2rem;
  color: #6b7280;
}

.empty-state {
  text-align: center;
  padding: 1.5rem;
}

.empty-icon {
  font-size: 2.5rem;
  color: #d1d5db;
  margin-bottom: 1rem;
}

.empty-state h3 {
  margin: 0 0 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
}

.empty-state p {
  margin: 0;
  color: #6b7280;
}

.table-wrap {
  overflow-x: auto;
}

.mappings-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.mappings-table th,
.mappings-table td {
  text-align: left;
  padding: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: top;
}

.mappings-table th {
  background: #f9fafb;
  font-weight: 600;
  color: #4b5563;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.05em;
}

.col-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  align-items: center;
  min-width: 0;
}

.mappings-table tr.row-off td:not(.col-actions):not(.col-availability) {
  opacity: 0.55;
}

.col-ids {
  width: 9rem;
}

.id-details summary {
  cursor: pointer;
  list-style: none;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.id-details summary::-webkit-details-marker {
  display: none;
}

.id-details summary::before {
  content: '';
  flex-shrink: 0;
  width: 0.4rem;
  height: 0.4rem;
  border-right: 2px solid #6b7280;
  border-bottom: 2px solid #6b7280;
  transform: rotate(-45deg);
}

.id-details[open] summary::before {
  transform: rotate(45deg);
}

.id-full {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-top: 0.5rem;
}

.id-line {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.id-label {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6b7280;
}

.id-line code.mono {
  word-break: break-all;
}

.col-availability {
  width: 1%;
  white-space: nowrap;
  vertical-align: middle;
  text-align: center;
}

.col-availability-label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
}

.info-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.15rem;
  height: 1.15rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  font-size: 0.8rem;
  text-transform: none;
  letter-spacing: normal;
}

.info-btn:hover,
.info-btn:focus-visible {
  color: #4b5563;
}

.info-btn::after {
  content: attr(data-tooltip);
  position: absolute;
  left: 50%;
  top: calc(100% + 8px);
  transform: translateX(-50%);
  width: max-content;
  max-width: 220px;
  padding: 0.4rem 0.55rem;
  border-radius: 6px;
  background: #111827;
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.35;
  letter-spacing: normal;
  text-transform: none;
  text-align: left;
  white-space: normal;
  opacity: 0;
  pointer-events: none;
  z-index: 30;
}

.info-btn:hover::after,
.info-btn:focus-visible::after {
  opacity: 1;
}

.availability-switch {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  flex-shrink: 0;
}

.availability-input {
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
  margin: 0;
}

.availability-track {
  position: relative;
  width: 2.5rem;
  height: 1.4rem;
  background: #d1d5db;
  border-radius: 999px;
  transition: background 0.2s ease;
}

.availability-thumb {
  position: absolute;
  top: 0.15rem;
  left: 0.15rem;
  width: 1.1rem;
  height: 1.1rem;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.2);
  transition: transform 0.2s ease;
}

.availability-input:focus-visible + .availability-track {
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.25);
}

.availability-input:checked + .availability-track {
  background: #10b981;
}

.availability-input:checked + .availability-track .availability-thumb {
  transform: translateX(1.1rem);
}

.availability-input:disabled + .availability-track {
  opacity: 0.6;
  cursor: not-allowed;
}

.availability-switch:has(.availability-input:disabled) {
  cursor: not-allowed;
}

.menu-item-name {
  font-weight: 500;
  color: #111827;
}

.menu-item-uuid {
  font-size: 0.75rem;
  color: #9ca3af;
  font-family: ui-monospace, monospace;
  margin-top: 0.25rem;
}

code.mono {
  font-family: ui-monospace, monospace;
  font-size: 0.8rem;
  padding: 0.15rem 0.4rem;
  background: #f3f4f6;
  border-radius: 0.25rem;
  color: #374151;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 0.75rem;
  padding: 2rem;
  max-width: 400px;
  width: 90%;
}

.modal-content.modal-wide {
  max-width: 560px;
}

.modal-content h3 {
  margin: 0 0 0.5rem;
  font-size: 1.25rem;
  font-weight: 600;
}

.modal-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1rem;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-label {
  display: block;
  margin-bottom: 0.25rem;
  font-weight: 500;
  color: #374151;
}

.form-help {
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0.25rem 0 0;
}

.form-input,
.form-select {
  width: 100%;
  padding: 0.6rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.95rem;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.error-banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  background-color: #fef2f2;
  border-radius: 0.5rem;
  color: #b91c1c;
  font-size: 0.875rem;
}

.muted {
  color: #6b7280;
}

@media (max-width: 768px) {
  .card-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .col-actions {
    flex-direction: column;
    min-width: auto;
  }
}
</style>
